import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { z } from 'zod';
import { mapKeys, mapValues } from 'lodash';
import { formatAmountWithCurrency } from '@fixter/i18n';
import type { SchemaType } from '$util/schema';
import { getSegmentFromLocale } from '$util/getSegment';
import { useAppContext } from '$util/AppContext';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import { useResizeViewport } from '$util/hooks/useResizeViewport';
import { extendCmsTextReact } from '$cms/index';
import type { ContentComponent } from '$cms/ContentComponent';
import { CmsLinkSchema, CmsNumberSchema, CmsRichTextSchema, CmsShortTextSchema } from '$cms/types';
import { Tag } from '$quantum/atoms/Tag';
import { Img } from '$src-components/atoms/Img';
import { ContentComponentSection } from '$src-components/atoms/ContentComponentSection';
import type { OurServicesCardsProps } from '$src-components/molecules/OurServicesCards';
import { OurServicesCards } from '$src-components/molecules/OurServicesCards';
import type { PopularLinksCardProps } from '$src-components/molecules/PopularLinksCard';
import { PopularLinksCard } from '$src-components/molecules/PopularLinksCard';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';

import { Icon } from '@/components/atoms/Icon';
import styles from './index.module.scss';

const backgroundLinesDeviceSrc = {
  mobile: '/OurServicesSection/our-services-double-line-mobile_akgrry.svg',
  desktop: '/OurServicesSection/our-services-double-line-desktop_nmd8fa.svg',
};

const OurServicesSectionSchema = {
  translatedEntities: z.object({
    heading: CmsShortTextSchema,
    cardTitle: z.record(z.union([CmsRichTextSchema, CmsShortTextSchema])),
    cardDescription: z.record(CmsRichTextSchema),
    cardCta: z.record(CmsShortTextSchema),
    popularTitle: z.record(CmsShortTextSchema),
    cardActiveTab: z.record(CmsShortTextSchema.optional()).optional(),
    popularViewAllLink: z.record(CmsLinkSchema),
    popularItem1Link: z.record(CmsLinkSchema),
    popularItem2Link: z.record(CmsLinkSchema),
    /**
     * Translatable placeholders used in RichText.
     */
    placeholderPrice: z.record(CmsNumberSchema.optional()).optional(),
    placeholderTagLimitedOffer: CmsShortTextSchema.optional(),
  }),
};

export type OurServicesSectionTranslatedEntities = SchemaType<
  (typeof OurServicesSectionSchema)['translatedEntities']
>;
export const OurServicesSection: ContentComponent<OurServicesSectionTranslatedEntities> =
  function OurServicesSection({ translatedEntities, className, children, ...props }) {
    const { locale } = useAppContext<AppContextGlobal>();

    const segment = getSegmentFromLocale(locale);

    const {
      heading,
      cardTitle,
      cardDescription,
      cardActiveTab,
      cardCta,
      popularTitle,
      popularViewAllLink,
      popularItem1Link,
      popularItem2Link,
      placeholderPrice = {},
      placeholderTagLimitedOffer,
    } = translatedEntities;

    const placeholders = useMemo(() => {
      const prices = mapValues(
        mapKeys(placeholderPrice, (price, i) => `price${i}`),
        (price) => price?.number
      );
      const tagLimitedOffer = placeholderTagLimitedOffer?.value;
      return {
        prices,
        tagLimitedOffer,
      };
    }, [placeholderPrice, placeholderTagLimitedOffer?.value]);

    const cardItems: OurServicesCardsProps['items'] = useMemo(
      () =>
        Object.entries(cardTitle).flatMap(([index, title]) => {
          if (!title) return [];
          const { prices, tagLimitedOffer } = placeholders;
          const description = renderRichTextContent(cardDescription[index]?.content, {
            renderText: (text: string) =>
              extendCmsTextReact(text, {
                // price1, price2, ...
                ...mapValues(
                  prices,
                  (price, key) =>
                    price && (
                      <strong className={styles.price} key={key}>
                        {formatAmountWithCurrency(price, segment, 0)}
                      </strong>
                    )
                ),
                tagLimitedOffer: tagLimitedOffer && (
                  <Tag className={styles.Tag} key="tagLimitedOffer">
                    <Icon id="finance/tag-01-flipped" width={16} height={16} legacy={false} />
                    {tagLimitedOffer}
                  </Tag>
                ),
              }),
          });
          return {
            title: 'content' in title ? renderRichTextContent(title.content) : title.value,
            description,
            cta: cardCta[index]?.value,
            activeTab: cardActiveTab && cardActiveTab[index]?.value,
          };
        }),
      [cardCta, cardDescription, cardTitle, placeholders, segment, cardActiveTab]
    );

    const popularItems: PopularLinksCardProps['items'] = useMemo(() => {
      const itemsLinks = new Map([
        [1, popularItem1Link],
        [2, popularItem2Link],
      ]);
      return Object.entries(popularTitle).map(([index, title]) => ({
        title: title.value,
        links: Object.values(itemsLinks.get(Number(index)) ?? {}),
        /**
         * If URL is not set for FR it defaults to UK, so for empty set `-`.
         */
        viewAllLink: popularViewAllLink[index]?.url !== '-' ? popularViewAllLink[index] : undefined,
      }));
    }, [popularItem1Link, popularItem2Link, popularTitle, popularViewAllLink]);

    const [backgroundLinesSrc, setBackgroundLinesSrc] = useState(backgroundLinesDeviceSrc.mobile);
    useResizeViewport({
      breakpoint: 'lg',
      fn: (isDesktop) => {
        setBackgroundLinesSrc(isDesktop ? backgroundLinesDeviceSrc.desktop : backgroundLinesDeviceSrc.mobile);
      },
    });

    return (
      <ContentComponentSection
        className={classNames(OurServicesSection.displayName, styles.component, className)}
        {...props}
      >
        <Img
          className={styles.backgroundLines}
          provider="cloudinary"
          src={backgroundLinesSrc}
          alt="Double line"
          fill
        />
        <OurServicesCards heading={heading.value} items={cardItems} />
        <PopularLinksCard className={styles.PopularLinksCard} items={popularItems} />
      </ContentComponentSection>
    );
  };
OurServicesSection.displayName = 'OurServicesSection';
OurServicesSection.schema = OurServicesSectionSchema;
